import React, { useState } from 'react';

import { useLocation, useNavigate } from 'react-router-dom';

import { 
  HomeIcon, 
  UsersIcon, 
  DocumentTextIcon,
  PhoneIcon,
  MicrophoneIcon,
  MapIcon,
  CogIcon,
  UserIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
  MagnifyingGlassIcon,
  BellIcon,
  CurrencyDollarIcon,
  DocumentChartBarIcon,
  BuildingOfficeIcon
} from '@heroicons/react/24/outline';

import { useAuth } from '../../../context/AuthContext';

// Crown Icon Component
const CrownIcon = ({ size = 24, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" className={className} fill="currentColor">
    <path d="M5 16L3 8l2.5 2L12 4l6.5 6L21 8l-2 8H5zm2.5-7L6 8l1 4h10l1-4-1.5 1L12 7 7.5 9z"/>
  </svg>
);

// Navigation Items
const navigationItems = [
  {
    name: 'Home',
    icon: HomeIcon,
    href: '/admin',
    path: '/admin'
  },
  {
    name: 'BOA',
    icon: BuildingOfficeIcon,
    href: '/admin/boa',
    path: '/admin/boa'
  },
  {
    name: 'Duck Charges',
    icon: CurrencyDollarIcon,
    href: '/admin/duck-charges',
    path: '/admin/duck-charges',
    badge: '3'
  },
  {
    name: 'Federal Bonding Costs',
    icon: DocumentChartBarIcon,
    href: '/admin/federal-bonding',
    path: '/admin/federal-bonding',
    badge: '2'
  },
  {
    name: 'Manual CRM',
    icon: UsersIcon,
    href: '/admin/manual-crm',
    path: '/admin/manual-crm',
    badge: '5'
  },
  {
    name: 'Call the Ducks',
    icon: PhoneIcon,
    href: '/admin/call-ducks',
    path: '/admin/call-ducks',
    badge: '7'
  },
  {
    name: 'Create Voicemail',
    icon: MicrophoneIcon,
    href: '/admin/create-voicemail',
    path: '/admin/create-voicemail',
    badge: '1'
  },
  {
    name: 'Prep a Duck',
    icon: MapIcon,
    href: '/admin/prep-duck',
    path: '/admin/prep-duck',
    badge: '4'
  },
  {
    name: 'Duck Worker Scripts',
    icon: DocumentTextIcon,
    href: '/admin/duck-scripts',
    path: '/admin/duck-scripts'
  }
];

const bottomNavigationItems = [
  {
    name: 'Profile',
    icon: UserIcon,
    href: '/admin/profile',
    path: '/admin/profile'
  },
  {
    name: 'Settings',
    icon: CogIcon,
    href: '/admin/settings',
    path: '/admin/settings'
  },
  {
    name: 'Logout',
    icon: ArrowRightOnRectangleIcon,
    href: '/logout',
    path: '/logout'
  }
];

// Sidebar Component
const Sidebar = ({ sidebarOpen, setSidebarOpen }) => {
  const location = useLocation();

    const { logout } = useAuth();
    const navigate = useNavigate();
  return (
    <>
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="fixed inset-0 bg-black bg-opacity-75" onClick={() => setSidebarOpen(false)} />
        </div>
      )}

      {/* Sidebar */}
      <div className={`
        fixed inset-y-0 left-0 z-50 w-64 bg-gradient-to-b from-purple-900 via-purple-800 to-purple-900 
        transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-center h-16 px-4 border-b border-purple-700">
            <div className="flex items-center space-x-2">
              <CrownIcon size={28} className="text-yellow-500" />
              <span className="text-xl font-bold text-white">William Walker</span>
            </div>
          </div>

          {/* Search */}
          <div className="p-4 border-b border-purple-700">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Quick search..."
                className="w-full bg-purple-800 border border-purple-600 rounded-lg pl-10 pr-4 py-2 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
            {navigationItems.map((item) => (
              <a
                key={item.name}
                href={item.href}
                className={`
                  group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors duration-200
                  ${location.pathname === item.path 
                    ? 'bg-purple-700 text-white' 
                    : 'text-gray-300 hover:bg-purple-800 hover:text-white'
                  }
                `}
              >
                <item.icon className="mr-3 h-5 w-5" />
                <span className="flex-1">{item.name}</span>
                {item.badge && (
                  <span className="ml-3 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white bg-red-600 rounded-full">
                    {item.badge}
                  </span>
                )}
              </a>
            ))}
          </nav>

          {/* Bottom Navigation */}
            <div className="px-2 py-4 border-t border-purple-700 space-y-1">
            {bottomNavigationItems.map((item) => (
                item.name === 'Logout' ? (
                <button
                    key={item.name}
                    onClick={() => {
                    logout();
                    navigate('/admin/login');
                    }}
                    className="group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors duration-200 text-gray-300 hover:bg-purple-800 hover:text-white w-full text-left"
                >
                    <item.icon className="mr-3 h-5 w-5" />
                    {item.name}
                </button>
                ) : (
                    <a
                    key={item.name}
                    href={item.href}
                    className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${location.pathname === item.path ? 'bg-purple-700 text-white' : 'text-gray-300 hover:bg-purple-800 hover:text-white'}`}
                >
                    <item.icon className="mr-3 h-5 w-5" />
                    {item.name}
                </a>
                )
            ))}
            </div>
        </div>
      </div>
    </>
  );
};

// Header Component
const Header = ({ sidebarOpen, setSidebarOpen }) => {
  return (
    <header className="bg-gray-800 border-b border-gray-700 lg:pl-64">
      <div className="flex items-center justify-between h-16 px-4">
        {/* Mobile menu button */}
        <button
          type="button"
          className="lg:hidden text-gray-400 hover:text-white focus:outline-none focus:ring-2 focus:ring-inset focus:ring-purple-500"
          onClick={() => setSidebarOpen(!sidebarOpen)}
        >
          {sidebarOpen ? (
            <XMarkIcon className="h-6 w-6" />
          ) : (
            <Bars3Icon className="h-6 w-6" />
          )}
        </button>

        {/* Page title - will be dynamic */}
        <div className="lg:hidden">
          <div className="flex items-center space-x-2">
            <CrownIcon size={24} className="text-yellow-500" />
            <h1 className="text-lg font-semibold text-white">Admin Portal</h1>
          </div>
        </div>

        {/* Right side */}
        <div className="flex items-center space-x-4">
          {/* Notifications */}
          <button className="relative text-gray-400 hover:text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-gray-800">
            <BellIcon className="h-6 w-6" />
            <span className="absolute -top-1 -right-1 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white bg-red-600 rounded-full">
              3
            </span>
          </button>

          {/* User menu */}
          <div className="relative">
            <button className="flex items-center space-x-3 text-gray-400 hover:text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-gray-800">
              <img
                className="h-8 w-8 rounded-full border-2 border-purple-500"
                src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80"
                alt="User avatar"
              />
              <div className="hidden md:block text-left">
                <p className="text-sm font-medium text-white">William Walker</p>
                <p className="text-xs text-gray-400">Administrator</p>
              </div>
            </button>
          </div>
        </div>
      </div>
    </header>
  );
};

// Reusable Admin Layout Wrapper
const AdminLayoutWrapper = ({ children, currentPath }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();

  const { logout } = useAuth();
  return (
    <div className="flex h-screen bg-gray-900">
      <Sidebar 
        sidebarOpen={sidebarOpen} 
        setSidebarOpen={setSidebarOpen}
        currentPath={currentPath} 
      />
      
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
        
        {/* Main Content Area with proper scrolling */}
        <main className="flex-1 lg:pl-64 overflow-y-auto">
          <div className="min-h-screen bg-gradient-to-br from-purple-900 via-gray-900 to-black">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminLayoutWrapper;